import { AddCircleOutline, DeleteOutline, Tune } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnsweringClient, InstrumentDiscount } from "../clients/answeringClient";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { IInstrument } from "./controller";

interface Props {
  open: boolean;
  instrument?: IInstrument;
  onClose: () => void;
  onSaved?: (discounts: InstrumentDiscount[]) => void;
}

type SavingState = "idle" | "saving" | "success";

const client = new AnsweringClient();

type EditableDiscount = {
  minQty: string;
  maxQty: string;
  discountPct: string;
};

function mapToEditable(discounts: InstrumentDiscount[]): EditableDiscount[] {
  if (!discounts.length) {
    return [
      {
        minQty: "1",
        maxQty: "",
        discountPct: "0",
      },
    ];
  }

  return discounts.map((discount) => ({
    minQty: discount.minQty.toString(),
    maxQty: discount.maxQty === null ? "" : discount.maxQty.toString(),
    discountPct: discount.discountPct.toString(),
  }));
}

function mapToApiPayload(
  editingDiscounts: EditableDiscount[]
): { discounts: InstrumentDiscount[] } | { error: string } {
  if (!editingDiscounts.length) {
    return { error: "Defina ao menos uma faixa de desconto." };
  }

  const normalized: InstrumentDiscount[] = [];

  for (let index = 0; index < editingDiscounts.length; index++) {
    const item = editingDiscounts[index];

    const minQty = Number(item.minQty);
    if (
      !item.minQty.trim() ||
      Number.isNaN(minQty) ||
      minQty <= 0 ||
      !Number.isInteger(minQty)
    ) {
      return { error: `Quantidade mínima inválida na faixa ${index + 1}.` };
    }

    let maxQty: number | null = null;
    if (item.maxQty.trim()) {
      const parsedMax = Number(item.maxQty);
      if (Number.isNaN(parsedMax) || !Number.isInteger(parsedMax)) {
        return { error: `Quantidade máxima inválida na faixa ${index + 1}.` };
      }
      maxQty = parsedMax;
    }
    if (maxQty !== null && maxQty < minQty) {
      return {
        error: `Na faixa ${index + 1}, a quantidade máxima deve ser maior ou igual à quantidade mínima.`,
      };
    }

    const discountPct = Number(item.discountPct);
    if (
      item.discountPct.trim() === "" ||
      Number.isNaN(discountPct) ||
      discountPct < 0 ||
      discountPct > 100
    ) {
      return {
        error: `Percentual de desconto inválido na faixa ${index + 1}.`,
      };
    }

    normalized.push({
      minQty,
      maxQty,
      discountPct,
      unitPriceOverrideCents: null,
    });
  }

  for (let index = 1; index < normalized.length; index++) {
    const previous = normalized[index - 1];
    const current = normalized[index];

    if (current.minQty <= previous.minQty) {
      return {
        error: `A faixa ${index + 1} deve iniciar com uma quantidade maior que a faixa anterior.`,
      };
    }

    if (previous.maxQty === null) {
      return {
        error: "Somente a última faixa pode não possuir quantidade máxima.",
      };
    }

    if (current.minQty !== previous.maxQty + 1) {
      return {
        error: `A faixa ${index + 1} deve iniciar em ${previous.maxQty + 1}.`,
      };
    }
  }

  return { discounts: normalized };
}

async function withAccessToken<T>(
  task: (accessToken: string) => Promise<T>
): Promise<T> {
  const accessToken = localStorage.getItem("AccessToken");
  if (!accessToken) {
    logout();
    throw new Error("Missing access token");
  }

  try {
    return await task(accessToken);
  } catch (error: any) {
    const isForbidden = error?.response?.status === 403;
    if (!isForbidden) {
      throw error;
    }

    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      logout();
      throw error;
    }

    try {
      const refreshed = await client.refreshToken(refreshToken);
      const tokens = refreshed.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return await task(tokens.accessToken);
    } catch {
      logout();
      throw error;
    }
  }
}

export const InstrumentDiscountEditorModal: React.FC<Props> = ({
  open,
  instrument,
  onClose,
  onSaved,
}) => {
  const [discounts, setDiscounts] = useState<EditableDiscount[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [savingState, setSavingState] = useState<SavingState>("idle");
  const [missingInstrument, setMissingInstrument] = useState(false);
  const lastInstrumentId = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      setDiscounts([]);
      setValidationError(null);
      setSavingState("idle");
      setMissingInstrument(false);
      lastInstrumentId.current = null;
      return;
    }

    if (!instrument) {
      setDiscounts([]);
      setMissingInstrument(true);
      setValidationError(null);
      setSavingState("idle");
      lastInstrumentId.current = null;
      return;
    }

    const instrumentChanged = lastInstrumentId.current !== instrument.id;
    setMissingInstrument(false);
    setDiscounts(mapToEditable(instrument.discountRules ?? []));

    if (instrumentChanged) {
      setValidationError(null);
      setSavingState("idle");
    }

    lastInstrumentId.current = instrument.id;
  }, [open, instrument]);

  const handleChange = useCallback(
    (index: number, field: keyof EditableDiscount, value: string) => {
      setDiscounts((prev) => {
        const next = [...prev];
        next[index] = {
          ...next[index],
          [field]: value,
        };
        return next;
      });
      setValidationError(null);
      setSavingState("idle");
    },
    []
  );

  const handleAdd = useCallback(() => {
    setDiscounts((prev) => {
      const next = [...prev];
      let newMin = "1";
      if (next.length > 0) {
        const last = next[next.length - 1];
        if (!last.maxQty.trim()) {
          setValidationError(
            "Defina a quantidade máxima da faixa atual antes de adicionar outra."
          );
          return next;
        }
        const parsed = Number(last.maxQty);
        if (Number.isNaN(parsed)) {
          setValidationError("A quantidade máxima deve ser um número válido.");
          return next;
        }
        newMin = (parsed + 1).toString();
      }

      setValidationError(null);
      return [
        ...next,
        {
          minQty: newMin,
          maxQty: "",
          discountPct: "0",
        },
      ];
    });
  }, []);

  const handleRemove = useCallback((index: number) => {
    setDiscounts((prev) => {
      if (prev.length <= 1) {
        setValidationError(
          "É necessário manter pelo menos uma faixa de desconto."
        );
        return prev;
      }

      const next = [...prev];
      next.splice(index, 1);
      setValidationError(null);
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!instrument) {
      return;
    }

    const payloadResult = mapToApiPayload(discounts);

    if ("error" in payloadResult) {
      setValidationError(payloadResult.error);
      setSavingState("idle");
      return;
    }

    try {
      setSavingState("saving");
      await withAccessToken((token) =>
        client.updateInstrumentDiscounts(
          token,
          instrument.id,
          payloadResult.discounts
        )
      );

      setSavingState("success");
      setValidationError(null);
      if (onSaved) {
        onSaved(payloadResult.discounts);
      }
    } catch {
      setValidationError(
        "Não foi possível atualizar os descontos. Tente novamente em instantes."
      );
      setSavingState("idle");
    }
  }, [discounts, instrument, onSaved]);

  const title = useMemo(
    () =>
      instrument
        ? `Configurar descontos - ${instrument.title}`
        : "Configurar descontos",
    [instrument]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="modal"
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle variant="modal">
        <Tune />
        {title}
      </DialogTitle>
      <DialogContent variant="modal">
        <Typography sx={{ mb: { xs: 1.75, md: 2 } }}>
          Defina as faixas de quantidade e os percentuais aplicados para este
          instrumento.
        </Typography>

        {missingInstrument ? (
          <Alert severity="error">
            Não foi possível identificar o instrumento selecionado. Feche o modal e tente novamente.
          </Alert>
        ) : (
          <>
            {savingState === "success" && (
              <Alert severity="success" >
                Descontos atualizados com sucesso.
              </Alert>
            )}
            {validationError && (
              <Alert severity="error">
                {validationError}
              </Alert>
            )}

            <Box
              sx={{
                flex: 1,
                maxHeight: { xs: 260, md: 480 },
                overflowY: "auto",
                pr: { xs: 0, sm: 1 },
              }}
            >
              {discounts.length ? (
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr 1fr",
                    },
                  }}
                >
                  {discounts.map((discount, index) => (
                    <Paper
                      key={`discount_${index}`}
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "grey.200",
                        borderRadius: 2,
                        p: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        backgroundColor: "grey.50",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        color="primary"
                      >
                        Faixa {index + 1}
                      </Typography>
                      <TextField
                        label="Quantidade mínima"
                        type="number"
                        size="small"
                        fullWidth
                        value={discount.minQty}
                        onChange={(event) =>
                          handleChange(index, "minQty", event.target.value)
                        }
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="Quantidade máxima"
                        type="number"
                        size="small"
                        fullWidth
                        value={discount.maxQty}
                        onChange={(event) =>
                          handleChange(index, "maxQty", event.target.value)
                        }
                        placeholder="Sem limite"
                        inputProps={{ min: 1 }}
                      />
                      <TextField
                        label="% de desconto"
                        type="number"
                        size="small"
                        fullWidth
                        value={discount.discountPct}
                        onChange={(event) =>
                          handleChange(index, "discountPct", event.target.value)
                        }
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                      <Box display="flex" justifyContent="flex-end">
                        <Button
                          color="error"
                          variant="text"
                          startIcon={<DeleteOutline fontSize="small" />}
                          onClick={() => handleRemove(index)}
                          disabled={discounts.length <= 1}
                        >
                          Remover faixa
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    textAlign: "center",
                    borderStyle: "dashed",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography color="text.secondary">
                    Nenhuma faixa de desconto cadastrada ainda.
                  </Typography>
                </Paper>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      {!missingInstrument && (
        <DialogActions
          variant="modal"
              sx={{display: "flex", justifyContent:"space-between", flexWrap: "wrap", gap: 1 }}
        >
          
              <Button
                variant="text"
                startIcon={<AddCircleOutline />}
                onClick={handleAdd}
              >
                Adicionar faixa
              </Button>
              <Box display="flex" gap={1}>
                <Button variant="outlined" color="error" onClick={onClose}>
                  Fechar
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={savingState === "saving"}
                >
                  {savingState === "saving" ? "Salvando..." : "Salvar"}
                </Button>
              </Box>
            
        </DialogActions>
      )}
    </Dialog>
  );
};
